import {
  INQUIRY_STATUS,
  INQUIRY_TYPE,
  INQUIRY_STATUS_OPTION,
  PRICING_ICON,
} from '@/types'
import { P, match } from 'ts-pattern'
import { DefineComponent, SVGAttributes, defineAsyncComponent } from 'vue'
import {
  inquiryStatuses,
  reservationInquiryStatuses,
  otherInquiryStatuses,
  otherCustomInquiryStatuses,
  jobListingStatuses,
  pickUpOrderStatuses,
  deliveryOrderStatuses,
} from '@/constants'

export function matchInquiryTypeToStatusList(inquiry: INQUIRY_TYPE) {
  return match(inquiry)
    .with('Catering', 'Private Party', () => inquiryStatuses)
    .with('Reservation', () => reservationInquiryStatuses)
    .with('Other Custom', 'Unknown', () => otherCustomInquiryStatuses)
    .with('Other', () => otherInquiryStatuses)
    .with('Job Listings', () => jobListingStatuses)
    .with('Delivery Order', () => deliveryOrderStatuses)
    .with('Pick Up Order', () => pickUpOrderStatuses)
    .otherwise(() => []) as INQUIRY_STATUS[]
}

const iconComponentCache = new Map<string, DefineComponent<SVGAttributes>>()

export function matchInquiryTypeToIcon(inquiryType: INQUIRY_TYPE) {
  const cacheKey = inquiryType.toLowerCase().replaceAll(' ', '_')

  if (iconComponentCache.has(cacheKey)) {
    return iconComponentCache.get(cacheKey)!
  }

  const component = match(inquiryType)
    .with(P.string, () =>
      defineAsyncComponent<DefineComponent<SVGAttributes>>(
        () =>
          import(
            `@/assets/type/${inquiryType.toLowerCase().replaceAll(' ', '_')}.svg`
          ),
      ),
    )
    .exhaustive()

  iconComponentCache.set(cacheKey, component)
  return component
}

const statusIconComponentCache = new Map<
  string,
  DefineComponent<SVGAttributes>
>()

export function matchInquiryStatusToIcon(inquiryStatus: INQUIRY_STATUS) {
  const cacheKey = inquiryStatus!.toLowerCase().replaceAll(' ', '_')

  if (statusIconComponentCache.has(cacheKey)) {
    return statusIconComponentCache.get(cacheKey)!
  }

  const component = defineAsyncComponent<DefineComponent<SVGAttributes>>(
    async () => {
      const normalized = inquiryStatus!.toLowerCase().replaceAll(' ', '_')
      try {
        return await import(`@/assets/status/${normalized}.svg`)
      } catch (e) {
        return await import('@/assets/status/unconfirmed.svg')
      }
    },
  )

  statusIconComponentCache.set(cacheKey, component)
  return component
}

export function matchInquiryTypeToShowType(inquiryType: INQUIRY_TYPE) {
  return match(inquiryType)
    .with('Catering', () => {
      return {
        label: 'Catering',
        shortLabel: 'Catering',
      }
    })
    .with('Reservation', () => {
      return {
        label: 'Reservations',
        shortLabel: 'Reservations',
      }
    })
    .with('Job Listings', () => {
      return {
        label: 'Job Applications',
        shortLabel: 'Jobs',
      }
    })
    .with('Delivery Order', () => {
      return {
        label: 'Orders',
        shortLabel: 'Orders',
      }
    })
    .with('Private Party', () => {
      return {
        label: 'Private Parties/Groups',
        shortLabel: 'Parties',
      }
    })
    .with('Other', () => {
      return {
        label: 'Other',
        shortLabel: 'Other',
      }
    })
    .with('Other Custom', () => {
      return {
        label: 'Other Custom',
        shortLabel: 'Other Custom',
      }
    })
    .with('Unknown', () => {
      return {
        label: 'Unknown',
        shortLabel: 'Unknown',
      }
    })
    .otherwise(() => {
      return {
        label: 'All Inquiries',
        shortLabel: 'All',
      }
    })
}

export function matchInquiryFilterToInquiryType(filterType: string) {
  return match(filterType)
    .with('Catering', () => 'Catering')
    .with('Reservations', () => 'Reservation')
    .with('Jobs', () => 'Job Listings')
    .with('Orders', () => 'Order')
    .with('Parties', () => 'Private Party')
    .with('Other', () => 'Other')
    .with('Other Custom', () => 'Other Custom')
    .with('Unknown', () => 'Unknown')
    .otherwise(() => 'All')
}

export function matchInquiryItemShowType(inquiryType: INQUIRY_TYPE) {
  return match(inquiryType)
    .with('Catering', () => 'Catering')
    .with('Reservation', () => 'Reservation')
    .with('Job Listings', () => 'Job Application')
    .with('Delivery Order', () => 'Delivery Order')
    .with('Pick Up Order', () => 'Pick-Up Order')
    .with('Private Party', () => 'Private Party/Group')
    .with('Other', () => 'Other')
    .with('Other Custom', () => 'Other Custom')
    .with('Unknown', () => 'Unknown')
    .exhaustive()
}

export function matchHighlightedInquiryStatus(status: INQUIRY_STATUS) {
  return match(status)
    .with('applied', () => true)
    .with('waiting on you', () => true)
    .with('unconfirmed', () => true)
    .with('received', () => true)
    .otherwise(() => false)
}

export function matchInquiryStatusToCssVariable(status: INQUIRY_STATUS) {
  return match(status!)
    .with('received', 'autoresponse sent', () => 'autoresponse_sent')
    .with(
      'unconfirmed',
      'waiting on you',
      'accepted',
      'accepted, arrange pickup',
      'applied',
      () => 'unconfirmed',
    )
    .with(
      'confirmed',
      'picked up',
      'interviewed',
      'hired',
      'delivered',
      'resolved',
      () => 'confirmed',
    )
    .with(
      'waiting on customer',
      'ready for pick up',
      'scheduled for interview',
      () => 'waiting_on_customer',
    )
    .with(
      'paused',
      'follow up in the future',
      'no showed interview',
      'not hired',
      'in route',
      () => 'paused',
    )
    .with(
      'cancelled',
      'no showed',
      'not a good fit',
      'rejected',
      () => 'cancelled',
    )
    .otherwise(
      () => 'unconfirmed',
    )
}

export function matchInquiryTypeToNotificationType(inquiryType: INQUIRY_TYPE) {
  return match(inquiryType)
    .with('Catering', () => 'catering')
    .with('Reservation', () => 'reservations')
    .with('Job Listings', () => 'job_listings')
    .with('Delivery Order', 'Pick Up Order', () => 'orders')
    .with('Private Party', () => 'parties')
    .with('Other', 'Other Custom', () => 'other')
    .with('Unknown', () => 'unknown')
    .exhaustive()
}

export function matchInquiryTypeToInquirySettings(inquiryType: INQUIRY_TYPE) {
  return match(inquiryType)
    .with('Catering', () => 'catering_settings')
    .with('Reservation', () => 'reservation_settings')
    .with('Job Listings', () => 'job_listings_settings')
    .with('Delivery Order', 'Pick Up Order', () => 'order_configs')
    .with('Private Party', () => 'private_parties_settings')
    .with('Other', 'Other Custom', () => 'other')
    .with('Unknown', () => 'unknown')
    .exhaustive()
}

export function matchResponseTemplate(template: string) {
  return match(template)
    .with('manual-ask-for-info', () => 'Ask for Info')
    .with('manual-ask-for-confirmation', () => 'Ask for Confirmation')
    .with('manual-send-info', () => 'Send information')
    .with('auto', () => 'Auto')
    .with('manual-offer-alternative', () => 'Offer Alternative')
    .with('manual-from-scratch', () => 'Manual from scratch')
    .with('manual-decline', () => 'Decline')
    .with('manual-no-delivery', () => 'No Delivery')
    .with('manual-auto', () => 'Manual auto')
    .with('manual-all-set', () => 'All set')
    .with('manual-send-info', () => 'Send Info')
    .otherwise(() => 'Manual')
}

export function matchAllowedDDInquiryStatuses(
  status: INQUIRY_STATUS,
  inquiryStatusOptions: INQUIRY_STATUS_OPTION[],
) {
  return match(status)
    .with('accepted, arrange pickup', () =>
      inquiryStatusOptions.filter(
        (option: INQUIRY_STATUS_OPTION) =>
          option.label === 'accepted, arrange pickup' ||
          option.label === 'rejected',
      ),
    )
    .with('delivered', () =>
      inquiryStatusOptions.filter(
        (option: INQUIRY_STATUS_OPTION) =>
          option.label === 'delivered' || option.label === 'rejected',
      ),
    )
    .with('in route', () =>
      inquiryStatusOptions.filter(
        (option: INQUIRY_STATUS_OPTION) =>
          option.label === 'rejected' || option.label === 'in route',
      ),
    )
    .otherwise(() =>
      inquiryStatusOptions.filter(
        (option: INQUIRY_STATUS_OPTION) =>
          option.label === 'accepted, arrange pickup' ||
          option.label === 'rejected' ||
          option.label === 'received',
      ),
    )
}

export function showFormattedInquiryCreatedAt(inquiryType: INQUIRY_TYPE) {
  return match(inquiryType)
    .with('Other', 'Other Custom', 'Unknown', 'Job Listings', () => true)
    .otherwise(() => false)
}

const pricingIconComponentCache = new Map<
  string,
  DefineComponent<SVGAttributes>
>()

export function matchPricingIconToIcon(icon: PRICING_ICON) {
  if (pricingIconComponentCache.has(icon)) {
    return pricingIconComponentCache.get(icon)!
  }

  const component = defineAsyncComponent<DefineComponent<SVGAttributes>>(
    () => import(`@/assets/pricing/${icon}.svg`),
  )

  pricingIconComponentCache.set(icon, component)
  return component
}
